/**
 * StacDownloadService — client for the aviation data STAC download API.
 *
 * Wraps the chart_api STAC endpoint (/api/stac) to browse and stream
 * 28-day / 56-day AIRAC cycle data and non-cycle publications.
 */
import { ENDPOINTS } from '../config/environment';
import { getAuthHeader } from './AuthContext';

// ─── STAC types (minimal subset needed by the download UI) ────────────────────

export interface StacAsset {
  href: string;
  title?: string;
  type?: string;
  roles?: string[];
}

export interface StacItem {
  id: string;
  type: 'Feature';
  stac_version: string;
  collection?: string;
  properties: {
    datetime?: string | null;
    title?: string;
    cycle?: string;
    data_type?: string;
    file_size_bytes?: number;
    [key: string]: unknown;
  };
  assets: Record<string, StacAsset>;
  links: Array<{ rel: string; href: string; type?: string }>;
}

export interface StacCollection {
  id: string;
  title?: string;
  description?: string;
  extent?: {
    temporal?: { interval?: Array<[string | null, string | null]> };
  };
}

export interface StacSearchResult {
  type: 'FeatureCollection';
  features: StacItem[];
  numberMatched?: number;
  numberReturned?: number;
  links?: Array<{ rel: string; href: string }>;
  context?: { limit: number; returned: number; matched?: number };
}

export interface StacSearchParams {
  limit?: number;
  offset?: number;
  collections?: string[];
  query?: Record<string, unknown>;
  sortby?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

const BASE = ENDPOINTS.stac.catalog;

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { ...getAuthHeader(), 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`STAC API returned ${res.status} ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function getStacCatalog(): Promise<{ title?: string; description?: string }> {
  return fetchJson(`${BASE}/`);
}

export async function getStacCollections(): Promise<StacCollection[]> {
  const res = await fetchJson<{ collections?: StacCollection[] } | StacCollection[]>(`${BASE}/collections`);
  return Array.isArray(res) ? res : (res.collections ?? []);
}

export async function searchStacItems(params: StacSearchParams = {}): Promise<StacSearchResult> {
  return fetchJson<StacSearchResult>(ENDPOINTS.stac.search, {
    method: 'POST',
    body: JSON.stringify({ limit: 20, ...params }),
  });
}

/** Return the best download URL for a STAC item (prefers 'data' role, then 'download'). */
export function getPrimaryAssetUrl(item: StacItem): string | null {
  const assets = Object.values(item.assets);
  const data = assets.find((a) => a.roles?.includes('data'));
  if (data) return data.href;
  const download = assets.find((a) => a.roles?.includes('download'));
  if (download) return download.href;
  if (assets.length > 0) return assets[0].href;
  return null;
}

export function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function itemTitle(item: StacItem): string {
  return item.properties.title ?? item.id;
}
